import { z } from 'zod'
import { FONTS } from '../utils/fonts'
import { COMPONENT_PROPS } from '../timeline/components/componentProps'
import { LAYOUT_KEYS } from './layout'
import { themeConfigSchema, transcriptionStyleSchema } from './schemas'
import { FPS } from './video'

/**
 * RenderConfig v2: the persisted timeline a render is made from (#77).
 *
 * Seconds, not frames: frames are a view concern, and `timeline/frames.ts` is
 * the one place seconds become frames. References, not values: media is named
 * by `recordingId`/`assetId` and captions by the track they follow; URLs and
 * words are resolved at hydration (`hydrateTimeline`). This module is JSX-free
 * and DOM-free because `packages/server`, `packages/api` and `apps/trigger`
 * import it as a value.
 *
 * "v2" names the timeline-document family, as against the v1 `clipScene`.
 * Within it `version` is the document's GENERATION (#541):
 *
 * - `version: 2` (`renderConfigV2Schema`, below) — captions, speaker texts
 *   and pictures and the visualizer name the media TRACK they follow
 *   (`for` / `dynamic.for`). What every writer emits today.
 * - `version: 3` (`renderConfigV3Schema`, at the end; #541, #566) — a scene
 *   is its own item: a span on the one scenes track holding its `layout` and
 *   its source's window. Videos are `view`s of a scene; followers name in
 *   `follows` the item they read (a scene, or a free media item); anything
 *   else may name the scene it was placed for, or stay free. READ here
 *   already, so a deployed app, Trigger and render bundle accept it before
 *   anything writes it; the writers and the lift from `version: 2` arrive
 *   in #596. The design: #566, issuecomment-6437992062, amended by
 *   issuecomment-6481168311.
 *
 * Two fields are pinned for now and carried so lifting them later is a schema
 * change rather than a data migration: `fps` is `FPS` (every composition and
 * `chunkFrameRange` hard-code it) and `playbackRate` is 1.
 */

// `typeIdSchema`'s check (`@zealous/shared`), kept local so the Lambda bundle
// takes no workspace dependency: the prefix and at least one character after it.
const prefixedId = <P extends string>(prefix: P) =>
  z.custom<`${P}_${string}`>(
    (v) =>
      typeof v === 'string' &&
      v.startsWith(`${prefix}_`) &&
      v.length > prefix.length + 1,
    { message: `Expected a ${prefix}_ id` },
  )

export const recordingIdSchema = prefixedId('rec')
export const assetIdSchema = prefixedId('ass')
export const highlightIdSchema = prefixedId('hig')
/**
 * A recording's participant (#566): the one key a speaker has in a render.
 * Every transcript utterance names its speaker by participant, across every
 * recording kind, so a speaker pin stores this and nothing else.
 */
export const participantIdSchema = prefixedId('par')
export type ParticipantId = z.infer<typeof participantIdSchema>

const finite = z.number().finite()
const seconds = finite.min(0)
const itemId = z.string().min(1).max(64)

export const MAX_DIMENSION = 4096
export const MAX_VOLUME = 4

export const mediaSourceSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('recording'),
    recordingId: recordingIdSchema,
  }),
  z.strictObject({ kind: z.literal('asset'), assetId: assetIdSchema }),
])
export type MediaSource = z.infer<typeof mediaSourceSchema>

export const transformSchema = z.strictObject({
  x: finite,
  y: finite,
  width: finite.positive(),
  height: finite.positive(),
  rotation: finite.optional(),
  opacity: finite.min(0).max(1).optional(),
  borderRadius: finite.min(0).optional(),
  crop: z
    .strictObject({
      left: finite.min(0),
      top: finite.min(0),
      right: finite.min(0),
      bottom: finite.min(0),
    })
    .optional(),
})
export type Transform = z.infer<typeof transformSchema>

export const fadeSchema = z.strictObject({
  in: finite.positive().optional(),
  out: finite.positive().optional(),
})
export type Fade = z.infer<typeof fadeSchema>

const FONT_FAMILIES = new Set(FONTS.map((font) => font.family))
export const fontFamilySchema = z
  .string()
  .refine((family) => FONT_FAMILIES.has(family), {
    message: 'Unknown font family',
  })

export const textItemStyleSchema = z.strictObject({
  fontFamily: fontFamilySchema,
  fontWeight: z.number().int().min(100).max(900),
  fontStyle: z.enum(['normal', 'italic']).optional(),
  fontSize: finite.positive(),
  lineHeight: finite.positive(),
  letterSpacing: finite,
  color: z.string().min(1),
  align: z.enum(['left', 'center', 'right']),
  strokeWidth: finite.min(0).optional(),
  strokeColor: z.string().optional(),
  /**
   * The most lines the text shows (#546): what does not fit is cut, as the
   * v1 layouts clamped a long title. Absent, the text wraps freely.
   */
  maxLines: z.number().int().min(1).optional(),
  /** A box behind each line, padded left and right, with rounded corners. */
  background: z
    .strictObject({
      color: z.string().min(1),
      padding: finite.min(0),
      radius: finite.min(0),
    })
    .optional(),
})
export type TextItemStyle = z.infer<typeof textItemStyleSchema>

export const transitionSchema = z.strictObject({ type: z.literal('cut') })

/**
 * A theme-axis field the user set by hand on an item (#547): a layout switch
 * keeps it, where an unpicked one takes the new layout's theme mapping, and a
 * theme pick clears it. Names, not paths: `strokeColor` is a solid's stroke
 * colour and a text's whole stroke, `background` a text's line boxes and a
 * caption's box. The render ignores it.
 */
export const themeFieldSchema = z.enum([
  'color',
  'fontFamily',
  'strokeColor',
  'background',
  'highlightColor',
  'highlightBgColor',
  'unPlayedColor',
  'textStrokeColor',
  'borderColor',
])
export type ThemeField = z.infer<typeof themeFieldSchema>

const itemBase = {
  id: itemId,
  from: seconds,
  duration: finite.positive(),
  transitionIn: transitionSchema.optional(),
  picked: z.array(themeFieldSchema).optional(),
}

const mediaItemFields = {
  source: mediaSourceSchema,
  /** Where the window this item plays starts, in its source's time. */
  sourceStart: seconds,
  /**
   * Where the window ends, recording items only (#77). The item plays the
   * window minus the transcript's cuts, so `duration` (the kept length) is
   * re-derived from the transcript at open, focus and render
   * (`timing/sceneSync.ts`). Absent on an earlier document: the window is then
   * `sourceStart + duration`.
   */
  sourceEnd: seconds.optional(),
  playbackRate: z.literal(1).optional(),
  volume: finite.min(0).max(MAX_VOLUME).optional(),
  fade: fadeSchema.optional(),
  /** Provenance only: the highlight this scene was added from, if any. */
  highlightId: highlightIdSchema.optional(),
}

/**
 * How a video fills its box (#547): `cover` crops to fill it, `contain` shows
 * the whole picture over a backdrop. Absent is Auto: contain when the video's
 * aspect differs from its box, cover when it matches (`timeline/fit.tsx`).
 */
export const videoFitSchema = z.enum(['cover', 'contain'])
export type VideoFit = z.infer<typeof videoFitSchema>

/** What fills the box around a fitted video; absent is `blur`. */
export const backdropSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('blur') }),
  z.strictObject({ kind: z.literal('color'), color: z.string().min(1) }),
])
export type Backdrop = z.infer<typeof backdropSchema>

export const videoItemSchema = z.strictObject({
  ...itemBase,
  type: z.literal('video'),
  ...mediaItemFields,
  transform: transformSchema.optional(),
  fit: videoFitSchema.optional(),
  backdrop: backdropSchema.optional(),
  /**
   * The sound's fade when it differs from the picture's (`fade`). Absent,
   * `fade` fades both, which is what every earlier document means.
   */
  audioFade: fadeSchema.optional(),
})

export const audioItemSchema = z.strictObject({
  ...itemBase,
  type: z.literal('audio'),
  ...mediaItemFields,
})

export const imageItemSchema = z.strictObject({
  ...itemBase,
  type: z.literal('image'),
  source: z.strictObject({ kind: z.literal('asset'), assetId: assetIdSchema }),
  transform: transformSchema.optional(),
  fade: fadeSchema.optional(),
})

/**
 * A text whose words come from the document at render time: the name or
 * handle of whoever is speaking on track `for`, per the transcript's speaker
 * spans. `text` is then what shows when nobody is known (the host).
 */
export const dynamicTextSchema = z.strictObject({
  source: z.literal('speaker'),
  field: z.enum(['name', 'handle']),
  for: itemId,
})
export type DynamicText = z.infer<typeof dynamicTextSchema>

export const textItemSchema = z.strictObject({
  ...itemBase,
  type: z.literal('text'),
  text: z.string(),
  style: textItemStyleSchema,
  transform: transformSchema.optional(),
  fade: fadeSchema.optional(),
  dynamic: dynamicTextSchema.optional(),
})

export const solidItemSchema = z.strictObject({
  ...itemBase,
  type: z.literal('solid'),
  color: z.string().min(1),
  /**
   * A border drawn inside the box (#546): the speech box, pill and episode
   * card the v1 layouts outlined. `width` is in composition pixels.
   */
  stroke: z
    .strictObject({ color: z.string().min(1), width: finite.positive() })
    .optional(),
  transform: transformSchema.optional(),
  fade: fadeSchema.optional(),
})

/**
 * Captions follow the media items of the track they are bound to. Their
 * `from`/`duration` are DERIVED from that track on every write (the server
 * normalizes them), never authored — which is why the editor renders them
 * locked.
 */
export const captionsItemSchema = z.strictObject({
  ...itemBase,
  type: z.literal('captions'),
  for: itemId,
  style: transcriptionStyleSchema,
  transform: transformSchema.optional(),
})

function componentArm<K extends keyof typeof COMPONENT_PROPS>(componentId: K) {
  return z.strictObject({
    ...itemBase,
    type: z.literal('component'),
    componentId: z.literal(componentId),
    props: COMPONENT_PROPS[componentId],
    /** The media track a component reads (the audio visualizer), if any. */
    for: itemId.optional(),
    transform: transformSchema.optional(),
    fade: fadeSchema.optional(),
  })
}

/**
 * A registry component with its props. The union is keyed on `componentId`
 * and each arm carries that component's own props schema, so an unknown id or
 * misshapen props fail here, at the boundary, not on Lambda.
 * `registry.test.ts` checks every `COMPONENT_IDS` entry has an arm.
 */
export const componentItemSchema = z.discriminatedUnion('componentId', [
  componentArm('title-card'),
  componentArm('logo-outro'),
  componentArm('audio-visualizer'),
  componentArm('picture'),
])

export const timelineItemSchema = z.discriminatedUnion('type', [
  videoItemSchema,
  audioItemSchema,
  imageItemSchema,
  textItemSchema,
  solidItemSchema,
  captionsItemSchema,
  componentItemSchema,
])
export type TimelineItemV2 = z.infer<typeof timelineItemSchema>
export type MediaItemV2 = z.infer<
  typeof videoItemSchema | typeof audioItemSchema
>
export type CaptionsItemV2 = z.infer<typeof captionsItemSchema>
export type ComponentItemV2 = z.infer<typeof componentItemSchema>

export const trackSchema = z.strictObject({
  id: itemId,
  items: z.array(timelineItemSchema),
  hidden: z.boolean().optional(),
  muted: z.boolean().optional(),
})
export type TrackV2 = z.infer<typeof trackSchema>

const dimension = z.number().int().min(2).max(MAX_DIMENSION)

export const renderConfigV2Schema = z.strictObject({
  version: z.literal(2),
  fps: z.literal(FPS),
  size: z.strictObject({ width: dimension, height: dimension }),
  theme: themeConfigSchema,
  /**
   * Items name their own sources; there is no document-level recording. The
   * project a document belongs to carries `recordingId` as provenance only
   * (#524). May be empty between "New project" and the first scene.
   */
  tracks: z.array(trackSchema),
  /**
   * The template last applied (#77): its blueprint's items are what an
   * orientation switch re-applies. Absent on a document built by hand.
   */
  template: z.strictObject({ layout: z.enum(LAYOUT_KEYS) }).optional(),
})
export type RenderConfigV2 = z.infer<typeof renderConfigV2Schema>

/** Narrow a persisted `Render.config` (v1 or v2) to v2. */
export function isRenderConfigV2(config: unknown): config is RenderConfigV2 {
  return (
    typeof config === 'object' &&
    config !== null &&
    (config as { version?: unknown }).version === 2
  )
}

export function isMediaItem(item: TimelineItemV2): item is MediaItemV2 {
  return item.type === 'video' || item.type === 'audio'
}

/* ------------------------------------------------------------------------ */
/* Generation 3 (#541, #566): a scene is its own item.                      */
/* ------------------------------------------------------------------------ */

/** The generation the writers switch to; `renderConfigV2Schema` reads the one before. */
export const TIMELINE_CONFIG_VERSION = 3

export const layoutSchema = z.enum(LAYOUT_KEYS)

/**
 * The scene an item belongs to: which scene a layout placed it for. It may be
 * absent; an item without it is free and spans whatever it spans. A view
 * requires it (it shows that scene's picture). A follower never carries it:
 * it belongs to the scene of what it follows.
 */
const sceneField = { scene: itemId.optional() }

/**
 * The item a follower (captions, a speaker's name, handle or avatar, the
 * visualizer) reads: its words, speakers or sound, and the follower moves,
 * splits and goes with it (#566, decided 2026-10-28). Any item that plays a
 * source may be followed: a sourced scene, or a free video or audio item with
 * its own source, so a reaction or the second recording of a split view gets
 * its own captions. Which items qualify is the validator's rule
 * (`eligibleParent`), not this schema's, so a later source kind widens it
 * without a new field.
 */
const followsField = { follows: itemId.optional() }

/**
 * A scene: one shot of the storyboard, on the one scenes track. It holds its
 * `layout` and, when it plays something, the window of its source: the sound
 * plays once, from the scene, and every view of it shows that window, so two
 * crops of one recording cannot drift. A recording scene states `sourceEnd`
 * (the validator requires it; generation 3 has no legacy window). Without a
 * source it is a title card or an outro: a span the elements of its layout
 * belong to. It draws nothing itself.
 */
export const sceneItemSchema = z.strictObject({
  ...itemBase,
  type: z.literal('scene'),
  /** The layout the editor re-applies to the items that belong to it; the render never reads it. */
  layout: layoutSchema.optional(),
  source: mediaSourceSchema.optional(),
  sourceStart: seconds.optional(),
  sourceEnd: seconds.optional(),
  volume: finite.min(0).max(MAX_VOLUME).optional(),
  /** The sound's fade. A view's `fade` is its picture's. */
  fade: fadeSchema.optional(),
  /** Provenance only: the highlight this scene was added from, if any. */
  highlightId: highlightIdSchema.optional(),
})
export type SceneItem = z.infer<typeof sceneItemSchema>

/**
 * A view: where and how a scene's picture shows. No source and no timing of
 * its own: its `from`/`duration` are DERIVED from the scene on every write,
 * the way captions' are, so it always spans its whole scene. Two views of one
 * scene are the same moment of the same recording, cropped twice.
 */
export const viewItemSchema = z.strictObject({
  ...itemBase,
  type: z.literal('view'),
  scene: itemId,
  transform: transformSchema.optional(),
  fit: videoFitSchema.optional(),
  backdrop: backdropSchema.optional(),
  /** The picture's fade; the sound fades with the scene. */
  fade: fadeSchema.optional(),
})
export type ViewItem = z.infer<typeof viewItemSchema>

/**
 * A text whose words are the name or handle of whoever speaks in the item it
 * `follows`, or, pinned with `participantId`, of that one participant of the
 * followed recording (#566): a slot in a two-heads or two-avatars layout that
 * always names the same person and lights up while they talk.
 */
export const dynamicTextV3Schema = z.strictObject({
  source: z.literal('speaker'),
  field: z.enum(['name', 'handle']),
  participantId: participantIdSchema.optional(),
})
export type DynamicTextV3 = z.infer<typeof dynamicTextV3Schema>

/** A free video or audio item: b-roll, music, an uploaded clip. It keeps its own window. */
export const videoItemV3Schema = videoItemSchema.extend(sceneField)
export const audioItemV3Schema = audioItemSchema.extend(sceneField)
export const imageItemV3Schema = imageItemSchema.extend(sceneField)
export const textItemV3Schema = textItemSchema.extend({
  ...sceneField,
  ...followsField,
  dynamic: dynamicTextV3Schema.optional(),
})
export const solidItemV3Schema = solidItemSchema.extend(sceneField)
/**
 * Captions show the words of the one recording item they follow (a scene, or
 * a free recording video or audio); their `from`/`duration` are DERIVED from
 * it on every write. A cut inside it stays in one caption flow.
 */
export const captionsItemV3Schema = captionsItemSchema
  .omit({ for: true })
  .extend({ follows: itemId })
function componentArmV3<K extends keyof typeof COMPONENT_PROPS>(
  componentId: K,
) {
  return componentArm(componentId)
    .omit({ for: true })
    .extend({ ...sceneField, ...followsField })
}
export const componentItemV3Schema = z.discriminatedUnion('componentId', [
  componentArmV3('title-card'),
  componentArmV3('logo-outro'),
  componentArmV3('audio-visualizer'),
  /**
   * A speaker picture's pin (#566) sits on the item, beside `scene`, never in
   * `props`: `COMPONENT_PROPS.picture` is shared with generation 2 and strict.
   */
  componentArmV3('picture').extend({
    participantId: participantIdSchema.optional(),
  }),
])
export const timelineItemV3Schema = z.discriminatedUnion('type', [
  sceneItemSchema,
  viewItemSchema,
  videoItemV3Schema,
  audioItemV3Schema,
  imageItemV3Schema,
  textItemV3Schema,
  solidItemV3Schema,
  captionsItemV3Schema,
  componentItemV3Schema,
])
export type TimelineItemV3 = z.infer<typeof timelineItemV3Schema>
export type MediaItemV3 = z.infer<
  typeof videoItemV3Schema | typeof audioItemV3Schema
>
export type CaptionsItemV3 = z.infer<typeof captionsItemV3Schema>
export const trackV3Schema = trackSchema.extend({
  items: z.array(timelineItemV3Schema),
})
export type TrackV3 = z.infer<typeof trackV3Schema>
export const renderConfigV3Schema = renderConfigV2Schema.extend({
  version: z.literal(TIMELINE_CONFIG_VERSION),
  tracks: z.array(trackV3Schema),
})
export type RenderConfigV3 = z.infer<typeof renderConfigV3Schema>

/** A timeline document of either generation. */
export type TimelineConfig = RenderConfigV2 | RenderConfigV3
export type TimelineItemAny = TimelineItemV2 | TimelineItemV3
export type MediaItemAny = MediaItemV2 | MediaItemV3

export function isRenderConfigV3(config: unknown): config is RenderConfigV3 {
  return (
    typeof config === 'object' &&
    config !== null &&
    (config as { version?: unknown }).version === TIMELINE_CONFIG_VERSION
  )
}

/** Narrow a persisted `Render.config` to the timeline family — either generation. */
export function isTimelineConfig(config: unknown): config is TimelineConfig {
  return isRenderConfigV2(config) || isRenderConfigV3(config)
}

/** A free video or audio item, of either generation. */
export function isMediaItemAny(item: TimelineItemAny): item is MediaItemAny {
  return item.type === 'video' || item.type === 'audio'
}

/**
 * What an item plays or shows from storage: a free media item's, an image's,
 * a sourced scene's. `null` for everything else, a view included (its
 * scene's source is the one that is loaded). The one predicate every reader
 * of sources goes through, so a scene is never missed.
 */
export function itemSource(item: TimelineItemAny): MediaSource | null {
  if (isMediaItemAny(item) || item.type === 'image') return item.source
  if (item.type === 'scene') return item.source ?? null
  return null
}

/**
 * The window an item plays: a free media item's or a sourced scene's, with
 * its source. `null` for anything that plays no window.
 */
export function itemWindow(item: TimelineItemAny): {
  source: MediaSource
  sourceStart: number
  sourceEnd: number | undefined
  duration: number
} | null {
  if (isMediaItemAny(item))
    return {
      source: item.source,
      sourceStart: item.sourceStart,
      sourceEnd: item.sourceEnd,
      duration: item.duration,
    }
  if (item.type === 'scene' && item.source)
    return {
      source: item.source,
      sourceStart: item.sourceStart ?? 0,
      sourceEnd: item.sourceEnd,
      duration: item.duration,
    }
  return null
}
